import type { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { AppState } from 'index';

import { getTestIdByParts } from 'utils/getTestIdByParts';
import type { WithTestId } from 'types/app';

import { getOfferSimilarFeedAction } from '@Modules/offers/slice';
import { isServerError } from '@Modules/errors/utils';
import { selectOffersSimilar } from '@Modules/offers/selectors';

import { Feed } from '@Atomic/organisms/Feed';

type OfferSimilarItemsProps = WithTestId<{ offerId: number; className?: string }>;

export const OfferSimilarItems: FC<OfferSimilarItemsProps> = ({
  offerId,
  className,
  testId = 'offer',
}) => {
  const dispatch = useDispatch();

  const { data, paginator } = useSelector(selectOffersSimilar);
  const isLoading = useSelector((state: AppState) => state.loading[getOfferSimilarFeedAction.name]);
  const feedError = useSelector((state: AppState) => state.errors[getOfferSimilarFeedAction.name]);

  const loadSimilarOffers = (page: number): void => {
    dispatch(
      getOfferSimilarFeedAction.request({
        offerId: String(offerId),
        page,
        perPage: paginator.step,
      })
    );
  };

  const loadMoreSimilarOffersHandler = (): void => {
    loadSimilarOffers(paginator.current + 1);
  };

  const onRetryLoadSimilarOffersHandler = (): void => {
    const nextPage = data.length ? paginator.current + 1 : paginator.current;
    loadSimilarOffers(nextPage);
  };

  if (!data.length) {
    return null;
  }

  return (
    <Feed
      className={className}
      feed={data}
      showLoadMoreButton={paginator.nextPageExists}
      onLoadMore={loadMoreSimilarOffersHandler}
      onRetry={onRetryLoadSimilarOffersHandler}
      hasError={isServerError(feedError)}
      isLoading={isLoading}
      testId={getTestIdByParts(testId, 'similar')}
    />
  );
};
